/**
 * Files Service
 * Handles file upload, download, and management operations
 * Requires admin authorization for most operations
 */

import api from './apiClient';

/**
 * @typedef {Object} FileEntity
 * @property {number} id - File ID
 * @property {string} fileName - Original filename
 * @property {string} storagePath - Server storage path
 * @property {string} contentType - MIME type
 * @property {number} fileSize - Size in bytes
 * @property {string} description - File description
 * @property {string} altText - Alternative text for images
 * @property {string} uploadedById - User ID of uploader
 * @property {string} uploadedByEmail - Email of uploader
 * @property {string} dateCreated - Upload timestamp
 * @property {string} dateModified - Last modified timestamp
 */

/**
 * @typedef {Object} FilePagedResult
 * @property {FileEntity[]} items - Array of files
 * @property {number} totalCount - Total number of files
 * @property {number} page - Current page number
 * @property {number} pageSize - Items per page
 * @property {boolean} hasNextPage - Whether more pages exist
 */

/**
 * @typedef {Object} FileUploadResponse
 * @property {boolean} success - Whether upload succeeded
 * @property {string} message - Status message
 * @property {FileEntity} [file] - Uploaded file (single upload)
 * @property {FileEntity[]} [files] - Uploaded files (batch upload)
 */

/**
 * @typedef {Object} FileQueryParams
 * @property {number} [page=1] - Page number
 * @property {number} [pageSize=10] - Items per page
 * @property {string} [contentType] - Filter by content type
 * @property {string} [uploadedBy] - Filter by uploader ID
 * @property {string} [search] - Search in filename/description
 */

/**
 * Get paginated list of files
 * @param {FileQueryParams} [params={}] - Query parameters
 * @param {Object} [options] - Request options
 * @returns {Promise<FilePagedResult>}
 */
export const getFiles = async (params = {}, options = {}) => {
  return api.get('/files', params, options);
};

/**
 * Get a single file by ID
 * @param {number} id - File ID
 * @param {Object} [options] - Request options
 * @returns {Promise<FileEntity>}
 */
export const getFile = async (id, options = {}) => {
  return api.get(`/files/${id}`, {}, options);
};

/**
 * Download a file
 * @param {number} id - File ID
 * @param {Object} [options] - Request options
 * @returns {Promise<{blob: Blob, filename: string}>}
 */
export const downloadFile = async (id, options = {}) => {
  return api.download(`/files/${id}/download`, options);
};

/**
 * Trigger file download in browser
 * @param {number} id - File ID
 * @param {string} [customFilename] - Override filename
 */
export const downloadFileToBrowser = async (id, customFilename = null) => {
  const { blob, filename } = await downloadFile(id);
  
  // Create download link
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = customFilename || filename;
  document.body.appendChild(link);
  link.click();
  
  // Cleanup
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Upload a single file
 * @param {File} file - File to upload
 * @param {Object} [metadata={}] - File metadata
 * @param {string} [metadata.description] - File description
 * @param {string} [metadata.altText] - Alt text for images
 * @param {Function} [onProgress] - Progress callback (0-100)
 * @param {Object} [options] - Request options
 * @returns {Promise<FileUploadResponse>}
 */
export const uploadFile = async (file, metadata = {}, onProgress = null, options = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  
  if (metadata.description) {
    formData.append('description', metadata.description);
  }
  if (metadata.altText) {
    formData.append('altText', metadata.altText);
  }

  return api.upload('/files', formData, {
    ...options,
    onUploadProgress: onProgress 
      ? (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      : undefined,
  });
};

/**
 * Upload multiple files
 * @param {FileList|File[]} files - Files to upload
 * @param {Object} [metadata={}] - Shared metadata for all files
 * @param {string} [metadata.description] - Description for all files
 * @param {Function} [onProgress] - Progress callback (0-100)
 * @param {Object} [options] - Request options
 * @returns {Promise<FileUploadResponse>}
 */
export const uploadFiles = async (files, metadata = {}, onProgress = null, options = {}) => {
  const formData = new FormData();
  
  Array.from(files).forEach((file) => {
    formData.append('files', file);
  });
  
  if (metadata.description) {
    formData.append('description', metadata.description);
  }

  return api.upload('/files/batch', formData, {
    ...options,
    onUploadProgress: onProgress 
      ? (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      : undefined,
  });
};

/**
 * Delete a file
 * @param {number} id - File ID
 * @param {string} [reason] - Deletion reason
 * @param {Object} [options] - Request options
 * @returns {Promise<{message: string}>}
 */
export const deleteFile = async (id, reason = null, options = {}) => {
  const url = reason ? `/files/${id}?reason=${encodeURIComponent(reason)}` : `/files/${id}`;
  return api.delete(url, {
    ...options,
    successMessage: 'File deleted successfully',
  });
};

/**
 * Get file storage health information
 * @param {Object} [options] - Request options
 * @returns {Promise<Object>} Storage health info
 */
export const getStorageHealth = async (options = {}) => {
  return api.get('/files/health', {}, options);
};

/**
 * Get file deletion logs
 * @param {Object} [params={}] - Query parameters
 * @param {number} [params.page=1] - Page number
 * @param {number} [params.pageSize=10] - Items per page
 * @param {Object} [options] - Request options
 * @returns {Promise<Object>} Paginated deletion logs
 */
export const getDeletionLogs = async (params = {}, options = {}) => {
  return api.get('/files/deletion-logs', params, options);
};

/**
 * Cleanup orphaned database records (records without physical files)
 * @param {Object} [options] - Request options
 * @returns {Promise<{itemsDeleted: number, message: string}>}
 */
export const cleanupOrphanedRecords = async (options = {}) => {
  return api.post('/files/cleanup-orphans', {}, {
    ...options,
    successMessage: 'Orphaned records cleaned up successfully',
  });
};

/**
 * Delete a single ghost record (DB record without physical file)
 * @param {number} id - File ID
 * @param {Object} [options] - Request options
 * @returns {Promise<void>}
 */
export const deleteGhostRecord = async (id, options = {}) => {
  return api.delete(`/files/ghost-records/${id}`, {}, {
    ...options,
    successMessage: 'Ghost record deleted successfully',
  });
};

/**
 * Bulk delete multiple ghost records (DB records without physical files)
 * @param {number[]} ids - Array of file IDs
 * @param {Object} [options] - Request options
 * @returns {Promise<{itemsDeleted: number, itemsFailed: number, message: string}>}
 */
export const bulkDeleteGhostRecords = async (ids, options = {}) => {
  return api.post('/files/ghost-records/bulk-delete', { ids }, {
    ...options,
    successMessage: `Successfully deleted ${ids.length} ghost record(s)`,
  });
};

/**
 * Delete a single unassociated file (file not linked to any entity)
 * @param {number} id - File ID
 * @param {Object} [options] - Request options
 * @returns {Promise<void>}
 */
export const deleteUnassociatedFile = async (id, options = {}) => {
  return api.delete(`/files/unassociated/${id}`, {}, {
    ...options,
    successMessage: 'Unassociated file deleted successfully',
  });
};

/**
 * Bulk delete multiple unassociated files (files not linked to any entity)
 * @param {number[]} ids - Array of file IDs
 * @param {Object} [options] - Request options
 * @returns {Promise<{itemsDeleted: number, itemsFailed: number, message: string}>}
 */
export const bulkDeleteUnassociatedFiles = async (ids, options = {}) => {
  return api.post('/files/unassociated/bulk-delete', { ids }, {
    ...options,
    successMessage: `Successfully deleted ${ids.length} unassociated file(s)`,
  });
};

/**
 * Cleanup untracked files (physical files without database records)
 * @param {Object} [options] - Request options
 * @returns {Promise<{itemsDeleted: number, message: string}>}
 */
export const cleanupUntrackedFiles = async (options = {}) => {
  return api.post('/files/cleanup-untracked', {}, {
    ...options,
    successMessage: 'Untracked files cleaned up successfully',
  });
};

/**
 * Get file storage settings
 * @param {Object} [options] - Request options
 * @returns {Promise<Object>} Storage settings
 */
export const getStorageSettings = async (options = {}) => {
  return api.get('/files/settings', {}, options);
};

/**
 * Format file size for display
 * @param {number} bytes - Size in bytes
 * @returns {string} Formatted size (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get file extension from filename
 * @param {string} filename - Filename
 * @returns {string} Extension without dot (lowercase)
 */
export const getFileExtension = (filename) => {
  return filename.split('.').pop()?.toLowerCase() || '';
};

/**
 * Check if file is an image based on content type
 * @param {string} contentType - MIME type
 * @returns {boolean}
 */
export const isImage = (contentType) => {
  return contentType?.startsWith('image/') || false;
};

/**
 * Delete a single file deletion log
 * @param {number} id - Deletion log ID
 * @param {Object} [options] - Request options
 * @returns {Promise<void>}
 */
export const deleteDeletionLog = async (id, options = {}) => {
  return api.delete(`/files/deletion-logs/${id}`, {}, {
    ...options,
    successMessage: 'Deletion log removed successfully',
  });
};

/**
 * Bulk delete multiple file deletion logs
 * @param {number[]} ids - Array of deletion log IDs
 * @param {Object} [options] - Request options
 * @returns {Promise<{deletedCount: number, requestedCount: number, message: string}>}
 */
export const bulkDeleteDeletionLogs = async (ids, options = {}) => {
  return api.post('/files/deletion-logs/bulk-delete', { ids }, {
    ...options,
    successMessage: `Successfully deleted ${ids.length} deletion log(s)`,
  });
};

/**
 * Download/view an untracked file
 * @param {string} fileName - The filename of the untracked file
 * @param {Object} [options] - Request options
 * @returns {Promise<{blob: Blob, filename: string}>}
 */
export const downloadUntrackedFile = async (fileName, options = {}) => {
  return api.download(`/files/untracked/${encodeURIComponent(fileName)}`, options);
};

/**
 * Download untracked file to browser
 * @param {string} fileName - The filename of the untracked file
 */
export const downloadUntrackedFileToBrowser = async (fileName) => {
  const { blob, filename } = await downloadUntrackedFile(fileName);
  
  // Create download link
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || fileName;
  document.body.appendChild(link);
  link.click();
  
  // Cleanup
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Get URL for previewing an untracked file
 * @param {string} fileName - The filename of the untracked file
 * @returns {string} - The URL to access the untracked file
 */
export const getUntrackedFileUrl = (fileName) => {
  const baseUrl = process.env.REACT_APP_API_URL || '';
  return `${baseUrl}/Files/${encodeURIComponent(fileName)}`;
};

const filesService = {
  getFiles,
  getFile,
  downloadFile,
  downloadFileToBrowser,
  uploadFile,
  uploadFiles,
  deleteFile,
  getStorageHealth,
  getDeletionLogs,
  deleteDeletionLog,
  bulkDeleteDeletionLogs,
  cleanupOrphanedRecords,
  cleanupUntrackedFiles,
  deleteGhostRecord,
  bulkDeleteGhostRecords,
  deleteUnassociatedFile,
  bulkDeleteUnassociatedFiles,
  getStorageSettings,
  formatFileSize,
  getFileExtension,
  isImage,
  downloadUntrackedFile,
  downloadUntrackedFileToBrowser,
  getUntrackedFileUrl,
};

export default filesService;
